'use client';

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HorizontalCardRailProps {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
}

export function HorizontalCardRail({
  ariaLabel,
  children,
  className,
}: HorizontalCardRailProps) {
  const railId = useId();
  const railRef = useRef<HTMLDivElement>(null);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);

  const updateScrollState = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;

    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    setCanScrollPrevious(rail.scrollLeft > 4);
    setCanScrollNext(rail.scrollLeft < maxScrollLeft - 4);
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;

    updateScrollState();
    rail.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      rail.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [children, updateScrollState]);

  const moveToAdjacentCard = useCallback((direction: -1 | 1) => {
    const rail = railRef.current;
    if (!rail) return;

    const paddingLeft = Number.parseFloat(window.getComputedStyle(rail).paddingLeft) || 0;
    const positions = Array.from(rail.children)
      .filter((child): child is HTMLElement => child instanceof HTMLElement)
      .map((child) => Math.max(0, child.offsetLeft - paddingLeft));

    if (positions.length === 0) return;

    const currentIndex = positions.reduce((closestIndex, position, index) => {
      const closestDistance = Math.abs(positions[closestIndex] - rail.scrollLeft);
      const distance = Math.abs(position - rail.scrollLeft);
      return distance < closestDistance ? index : closestIndex;
    }, 0);
    const targetIndex = Math.max(
      0,
      Math.min(positions.length - 1, currentIndex + direction)
    );

    rail.scrollTo({
      behavior: 'smooth',
      left: positions[targetIndex],
    });
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveToAdjacentCard(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveToAdjacentCard(1);
    }
  };

  const controlClassName =
    'inline-flex h-10 w-10 items-center justify-center rounded-full border border-border-strong bg-bg-primary text-text-primary shadow-sm transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-border-strong disabled:hover:text-text-primary';

  return (
    <div>
      <div
        ref={railRef}
        id={railId}
        role="region"
        aria-label={ariaLabel}
        aria-roledescription="carrusel"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={cn('relative touch-auto', className)}
      >
        {children}
      </div>

      <div className="mt-2 flex items-center justify-end gap-2 lg:hidden">
        <button
          type="button"
          onClick={() => moveToAdjacentCard(-1)}
          disabled={!canScrollPrevious}
          aria-controls={railId}
          aria-label="Ver tarjeta anterior"
          className={controlClassName}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => moveToAdjacentCard(1)}
          disabled={!canScrollNext}
          aria-controls={railId}
          aria-label="Ver tarjeta siguiente"
          className={controlClassName}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
